import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { standardCommand } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import {
  LettingFeatureAdmin,
  LettingFeatureUsageCategory,
  LettingFeatureUsageRentalObject,
} from '../../model/letting-feature-admin';
import { RentalObjectCategory } from '../../model/const/enums';
import { LettingFeatureFormCommandHandlerResolver } from '../../service/command/letting-feature-form-command-handler-resolver.service';
import { LettingFeatureAdminService } from '../../service/data/letting-feature-admin.service';

@Component({
  selector: 'app-letting-feature-form',
  templateUrl: './letting-feature-form.component.html',
  styleUrls: ['./letting-feature-form.component.scss'],
  providers: [DeleteFormCommandHandler, LettingFeatureFormCommandHandlerResolver, { provide: DataService, useExisting: LettingFeatureAdminService }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingFeatureFormComponent extends FormComponentBase<LettingFeatureAdmin, LettingFeatureAdminService> {
  readonly usageCategories = [
    { category: RentalObjectCategory.Dwelling, labelKey: 'CONST.RENTAL_OBJECT_CATEGORY_DWELLING', iconClass: 'house-user' },
    { category: RentalObjectCategory.Premises, labelKey: 'CONST.RENTAL_OBJECT_CATEGORY_PREMISES', iconClass: 'store' },
    { category: RentalObjectCategory.Parking, labelKey: 'CONST.RENTAL_OBJECT_CATEGORY_PARKING', iconClass: 'car' },
    { category: RentalObjectCategory.Other, labelKey: 'CONST.RENTAL_OBJECT_CATEGORY_OTHER', iconClass: 'house-blank' },
  ] as const;

  usageSummaryLoading = false;
  usageSummaryFailed = false;
  usageSummary: LettingFeatureUsageCategory[] = [];
  usageByCategory: Partial<Record<RentalObjectCategory, LettingFeatureUsageRentalObject[]>> = {};
  readonly usageLoadingCategories = new Set<RentalObjectCategory>();
  readonly usageFailedCategories = new Set<RentalObjectCategory>();
  readonly usageLoadedCategories = new Set<RentalObjectCategory>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formControlFactory: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: LettingFeatureFormCommandHandlerResolver,
    service: LettingFeatureAdminService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  protected createModel(): LettingFeatureAdmin {
    return new LettingFeatureAdmin();
  }

  protected override init(data: LettingFeatureAdmin): void {
    super.init(data);
    if (data?.id) {
      this.loadUsageSummary();
    }
  }

  usageCount(category: RentalObjectCategory): number {
    return this.usageSummary.find(item => item.category === category)?.count ?? 0;
  }

  usageShare(category: RentalObjectCategory): string {
    const total = this.usageSummary.reduce((sum, item) => sum + item.count, 0);
    return total > 0 ? `${(this.usageCount(category) / total) * 100}%` : '0%';
  }

  onUsageAccordionStateChanged(category: RentalObjectCategory, expanded: boolean): void {
    if (expanded) {
      this.loadUsage(category);
    }
  }

  loadUsageSummary(): void {
    if (!this.dataContext?.id || this.usageSummaryLoading) {
      return;
    }

    this.usageSummaryLoading = true;
    this.usageSummaryFailed = false;
    this.AddSubscription(
      this.dataService
        .getUsageSummary(this.dataContext.id)
        .pipe(finalize(() => (this.usageSummaryLoading = false)))
        .subscribe({
          next: summary => (this.usageSummary = summary),
          error: () => (this.usageSummaryFailed = true),
        })
    );
  }

  loadUsage(category: RentalObjectCategory): void {
    if (
      !this.dataContext?.id ||
      this.usageLoadedCategories.has(category) ||
      this.usageLoadingCategories.has(category)
    ) {
      return;
    }

    this.usageLoadingCategories.add(category);
    this.usageFailedCategories.delete(category);
    this.AddSubscription(
      this.dataService
        .getUsage(this.dataContext.id, category)
        .pipe(finalize(() => this.usageLoadingCategories.delete(category)))
        .subscribe({
          next: usage => {
            this.usageByCategory[category] = usage;
            this.usageLoadedCategories.add(category);
          },
          error: () => this.usageFailedCategories.add(category),
        })
    );
  }

  protected getFormGroup(): FormGroup {
    return this.formBuilder.group({
      phrase: this.formControlFactory.createWithOptions('LETTING_FEATURE.CAPTION', this.dataContext.phrase, { required: true }),
      isActive: this.formControlFactory.create('LETTING_FEATURE.IS_ACTIVE', this.dataContext.isActive),
    });
  }
}
